import { Link } from '@backstage/core-components';
import { parseEntityRef } from '@backstage/catalog-model';
import type { Visit } from '@backstage/plugin-home';
import { makeStyles } from '@material-ui/core';
import type { ComponentType } from 'react';
import {
  GlobeIcon,
  GridIcon,
  LinkIcon,
  UsersIcon,
} from '../../components/icons';
import { HomePanel } from './HomePanel';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 44,
    // The mock's 44px row sits above its hairline rather than absorbing it, so
    // the rule is outside the box; MUI's global border-box would eat a pixel a
    // row and shorten the panel.
    boxSizing: 'content-box',
    padding: '0 20px',
    borderBottom: '1px solid var(--hone-portal-hairline-soft)',
    color: 'inherit',
    '&:hover': {
      color: 'inherit',
      background: 'var(--hone-portal-fill-inset)',
    },
  },
  icon: {
    width: 15,
    height: 15,
    opacity: 0.55,
    display: 'block',
    flex: 'none',
  },
  // Entity names are identifiers, so they are set in mono like the rest of the
  // portal's ids.
  name: {
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: 12.5,
    color: 'var(--bui-fg-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  kind: {
    flex: 'none',
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--bui-fg-secondary)',
    background: 'var(--hone-portal-fill-inset)',
    borderRadius: 'var(--bui-radius-full)',
    padding: '2px 8px',
  },
  spacer: { flex: 1 },
  time: {
    flex: 'none',
    fontFamily: 'var(--bui-font-monospace)',
    fontSize: 11,
    color: 'var(--hone-portal-fg-tertiary)',
  },
});

const KIND_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  api: LinkIcon,
  user: UsersIcon,
  group: UsersIcon,
  domain: GlobeIcon,
  system: GlobeIcon,
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function relativeTime(timestamp: number): string {
  const elapsed = Date.now() - timestamp;
  if (elapsed < HOUR) return `${Math.max(1, Math.round(elapsed / MINUTE))}m`;
  if (elapsed < DAY) return `${Math.round(elapsed / HOUR)}h`;
  return `${Math.round(elapsed / DAY)}d`;
}

// Entity refs store the kind lower-cased, so it is title-cased for the pill.
function kindOf(visit: Visit): string {
  const kind = visit.entityRef
    ? parseEntityRef(visit.entityRef).kind
    : visit.pathname.split('/').filter(Boolean)[2] ?? 'entity';
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

export interface RecentlyViewedPanelProps {
  visits: Visit[];
  /** Defaults to the catalog, since that's the visited entities' home. Pass
   * `null` on a page (e.g. the Documentation landing page) where that link
   * would send the reader somewhere disconnected from what they were doing. */
  action?: { label: string; to: string } | null;
}

// Recently viewed (mock 1c), fed by the home plugin's visits API — the same data
// the stock `HomePageRecentlyVisited` card reads, in the portal's row grammar.
export function RecentlyViewedPanel({
  visits,
  action = { label: 'View all', to: '/catalog' },
}: RecentlyViewedPanelProps) {
  const classes = useStyles();

  return (
    <HomePanel
      title="Recently viewed"
      action={action ?? undefined}
      isEmpty={visits.length === 0}
      emptyText="Pages you open will show up here."
    >
      {visits.map(visit => {
        const kind = kindOf(visit);
        const Icon = KIND_ICONS[kind.toLowerCase()] ?? GridIcon;
        return (
          <Link
            key={visit.id}
            to={visit.pathname}
            underline="none"
            className={classes.row}
          >
            <Icon className={classes.icon} />
            <span className={classes.name}>{visit.name}</span>
            <span className={classes.kind}>{kind}</span>
            <span className={classes.spacer} />
            <span className={classes.time}>{relativeTime(visit.timestamp)}</span>
          </Link>
        );
      })}
    </HomePanel>
  );
}
